import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  getCommonTextFilterParams,
  getSetFilterParams,
} from '../../utils/agGridFilters';
import { DateFormatter } from '../../shared/controls/formatters';

// deterministic hash → avatar palette (same approach as the other reskinned lists)
const AVA_COLORS = ['#c96342', '#2e63a6', '#2c7a45', '#9a751c', '#6a4fae', '#b34480', '#54738f'];
const initials = (name) =>
  String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] || '')
    .join('')
    .toUpperCase() || '?';
const avaColor = (name) =>
  AVA_COLORS[
    String(name || '')
      .split('')
      .reduce((a, c) => a + c.charCodeAt(0), 0) % AVA_COLORS.length
  ];

const OwnerCell = (params) => {
  const name = params.value;
  if (!name) return <span className="gos-cell-dash">—</span>;
  return (
    <div className="gos-owner-cell">
      <span className="gos-ava sm" style={{ background: avaColor(name) }}>
        {initials(name)}
      </span>
      <span className="gos-owner-name">{name}</span>
    </div>
  );
};

const ActiveCell = (params) => {
  const on = !!params.value;
  return (
    <span className={'gos-badge gos-b-' + (on ? 'green' : 'gray')}>
      <span className="gos-pip" />
      {on ? 'Yes' : 'No'}
    </span>
  );
};

// GrowthOS re-skin: no Actions column (matches Quotes + Sales). Name is a link to
// the Price Sheet view page; edit/delete are reached from there. Cells restyled:
// Name link, Owner avatar, Pricing chip, Active badge; dates/currency plain.
const usePriceColumnDefs = ({ gridData, owner }) => {
  const commonTextFilterParams = useMemo(() => getCommonTextFilterParams(), []);
  const filterParams = useMemo(() => getSetFilterParams, []);

  const columnDefs = useMemo(() => {
    return [
      {
        headerName: 'Name',
        field: 'name',
        minWidth: 180,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        cellRenderer: (params) => {
          if (!params.value) return <span className="gos-cell-dash">—</span>;
          return (
            <Link
              to={`/products/priceSheet/viewPriceSheet?id=${params?.data?.id}`}
              state={{ ...params.data }}
              className="gos-lead-name"
            >
              {params.value}
            </Link>
          );
        },
      },
      {
        headerName: 'Owner',
        field: 'priceSheetOwner',
        minWidth: 190,
        filter: 'agSetColumnFilter',
        filterParams: filterParams(owner?.map((item) => item.display_name)),
        cellRenderer: OwnerCell,
      },
      {
        headerName: 'Pricing Model',
        field: 'priceModel',
        flex: 1,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        cellRenderer: (params) =>
          params.value ? <span className="gos-chip">{params.value}</span> : <span className="gos-cell-dash">—</span>,
      },
      {
        headerName: 'Start Date',
        field: 'startDate',
        flex: 1,
        valueFormatter: DateFormatter,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'End Date',
        field: 'endDate',
        flex: 1,
        valueFormatter: DateFormatter,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Currency',
        field: 'currencyPrice',
        flex: 1,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        cellRenderer: (params) =>
          params.value ? <span className="gos-cell-muted">{params.value}</span> : <span className="gos-cell-dash">—</span>,
      },
      {
        headerName: 'Discount Percent',
        field: 'discountPercent',
        flex: 1,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        cellRenderer: (params) => {
          const v = params.value;
          if (v === null || v === undefined || v === '') return <span className="gos-cell-dash">0%</span>;
          return <span className="gos-num-cell">{v}%</span>;
        },
      },
      {
        headerName: 'Discount Amount',
        field: 'discountAmount',
        flex: 1,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        cellRenderer: (params) => {
          const v = params.value;
          if (!v) return <span className="gos-cell-dash">0</span>;
          return <span className="gos-num-cell">{'$' + Number(v).toLocaleString()}</span>;
        },
      },
      {
        headerName: 'Active',
        field: 'productActive',
        width: 120,
        cellRenderer: ActiveCell,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: [true, false],
          valueFormatter: (params) => (params.value ? 'Yes' : 'No'),
          buttons: ['apply', 'reset'],
        },
      },
    ];
  }, [commonTextFilterParams, filterParams, gridData, owner]);

  return columnDefs;
};

export default usePriceColumnDefs;
